"use client";

import { Button } from "@dc/ui/components";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRef } from "react";
import { PenLinear } from "solar-icon-set";
import type { Citation } from "../types";

interface PageCitationButtonProps {
  citation: Citation;
  /** Optional citation ID search parameter name (e.g., 'citationId') */
  citationIdParam?: string;
  /** Optional callback to handle citation click, overrides default navigation behavior */
  onCitationClick?: (citation: Citation) => void;
}

/**
 * Component to render page citation buttons for single document chats
 * Shows "Page X" with a pen icon next to it
 */
export const PageCitationButton = ({ citation, citationIdParam, onCitationClick }: PageCitationButtonProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isProcessingRef = useRef(false);

  // Get the first page for navigation
  const pageNumber = citation.pages[0];

  // Format page text based on number of pages
  const pageText =
    citation.pages.length === 1
      ? `Page ${citation.pages[0]}`
      : citation.pages.length === 2
        ? `Page ${citation.pages[0]} & ${citation.pages[1]}`
        : `Page ${citation.pages[0]}-${citation.pages[citation.pages.length - 1]}`;

  const handleClick = async () => {
    // If custom click handler is provided, use it instead of default navigation
    if (onCitationClick) {
      onCitationClick(citation);
      return;
    }

    // Prevent double-clicks
    if (isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;

    try {
      // Dynamically import to avoid bundle size increase for basic usage
      const { getCitationHighlights, getEncodedHighlightsWithPage } = await import("../utils/citationHelpers");

      const highlights = getCitationHighlights(citation);
      const { encodedHighlights, page: highlightsPage } = getEncodedHighlightsWithPage(highlights);

      const params = new URLSearchParams(searchParams);
      const currentHighlight = params.get("highlight");
      const currentCitationId = citationIdParam ? params.get(citationIdParam) : undefined;
      const citationId = citationIdParam ? `${citation.fileId}-${citation.citationIndex}` : undefined;

      // If the same citation is already active (both highlight and citation ID match), remove it (toggle off)
      if (
        currentHighlight === encodedHighlights &&
        (!citationIdParam || currentCitationId === citationId) &&
        encodedHighlights
      ) {
        params.delete("highlight");
        if (citationIdParam) {
          params.delete(citationIdParam);
        }
        params.delete("page");
      } else {
        // Use highlights page if available, otherwise fall back to first citation page
        const targetPage = highlightsPage ?? pageNumber;
        params.set("page", targetPage.toString());

        // Add highlight and citation ID parameters if we have encoded highlights
        if (encodedHighlights) {
          params.set("highlight", encodedHighlights);
          if (citationIdParam && citationId) {
            params.set(citationIdParam, citationId);
          }
        } else {
          params.delete("highlight");
          if (citationIdParam) {
            params.delete(citationIdParam);
          }
        }
      }

      const url = `${pathname}?${params.toString()}`;
      router.push(url, { scroll: false });
    } finally {
      // Reset the processing flag after a delay to prevent accidental double-clicks
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000); // Increased from 300ms to 1000ms to prevent issues during page transitions
    }
  };

  return (
    <span className="inline-flex align-middle">
      <Button
        size="sm"
        variant="solid"
        color="default"
        className="inline-flex h-6 max-h-6 min-h-6 items-center gap-1 rounded-full bg-default-200 px-2"
        onPress={handleClick}
      >
        <span className="font-['Inter'] font-normal text-color-text-interactive-neutral-usual text-xs leading-4">
          {pageText}
        </span>
        <PenLinear size={12} className="text-color-text-interactive-neutral-usual" />
      </Button>
    </span>
  );
};
